/**
 * calendar-work-item-bridge.ts
 *
 * Projects WorkItems and CRM Deal entities into HerxCalendarItem[]
 * for display in UnifiedCalendarView. Color-codes by domain source.
 */

import type { HerxCalendarItem, CalendarItemSource, CalendarDomainColorKey } from './calendar-types.js';
import type { WorkItem } from '../../kanban/work-item-contracts.js';

export interface CrmDealCalendarEntry {
  readonly id: string;
  readonly title: string;
  readonly expectedCloseAt?: string;
  readonly nextActionAt?: string;
  readonly amount?: number;
  readonly stageId?: string;
  readonly contactName?: string;
}

export class CalendarWorkItemBridge {
  /**
   * Projects a list of WorkItems into HerxCalendarItem[] for calendar display.
   * Only items with dueAt or scheduledAt are projected.
   */
  projectWorkItems(items: readonly WorkItem[]): HerxCalendarItem[] {
    const result: HerxCalendarItem[] = [];
    for (const item of items) {
      const dateStr = item.scheduledAt ?? item.dueAt;
      if (!dateStr) continue;

      const startAt = new Date(dateStr).getTime();
      if (isNaN(startAt)) continue;

      let colorKey: CalendarDomainColorKey = 'action-planner';
      let source: CalendarItemSource = 'scrum-sprint';
      if (item.type === 'deal-follow-up' || item.type === 'customer-action' || item.source?.type === 'crm') {
        colorKey = 'crm';
        source = 'crm-followup';
      }

      const isOverdue = item.dueAt
        ? new Date(item.dueAt).getTime() < Date.now() && item.statusId !== 'done'
        : false;

      result.push({
        eventId: `wi-${item.id}`,
        sourceId: item.id,
        source,
        title: item.title,
        description: item.description,
        startAt,
        endAt: startAt + 30 * 60 * 1000, // 30-min default
        dueAt: item.dueAt ? new Date(item.dueAt).getTime() : undefined,
        allDay: !item.scheduledAt,
        colorKey: isOverdue ? 'overdue' : colorKey,
        isOverdue,
        priority: item.priority as HerxCalendarItem['priority'],
        participants: [...item.assigneeIds],
        metadata: { workItemId: item.id, statusId: item.statusId },
      });
    }
    return result;
  }

  /**
   * Projects CRM Deal entries into HerxCalendarItem[] for appointment/milestone tracking.
   */
  projectCrmDeals(deals: readonly CrmDealCalendarEntry[]): HerxCalendarItem[] {
    const result: HerxCalendarItem[] = [];
    for (const deal of deals) {
      if (deal.expectedCloseAt) {
        const startAt = new Date(deal.expectedCloseAt).getTime();
        if (!isNaN(startAt)) {
          result.push({
            eventId: `deal-close-${deal.id}`,
            sourceId: deal.id,
            source: 'crm-followup',
            title: `🏁 ${deal.title} — Expected Close`,
            startAt,
            endAt: startAt,
            allDay: true,
            colorKey: startAt < Date.now() ? 'overdue' : 'crm',
            isOverdue: startAt < Date.now(),
            description: deal.contactName ? `Contact: ${deal.contactName}` : undefined,
            metadata: { dealId: deal.id, stage: deal.stageId },
          });
        }
      }
      if (deal.nextActionAt) {
        const startAt = new Date(deal.nextActionAt).getTime();
        if (!isNaN(startAt)) {
          result.push({
            eventId: `deal-action-${deal.id}`,
            sourceId: deal.id,
            source: 'crm-followup',
            title: `📞 ${deal.title} — Follow-Up`,
            startAt,
            endAt: startAt + 30 * 60 * 1000,
            allDay: false,
            colorKey: startAt < Date.now() ? 'overdue' : 'appointment',
            isOverdue: startAt < Date.now(),
            description: deal.contactName ? `Contact: ${deal.contactName}` : undefined,
            metadata: { dealId: deal.id },
          });
        }
      }
    }
    return result;
  }

  /**
   * Merges work-item and CRM projections into a single calendar feed.
   */
  buildCalendarFeed(
    workItems: readonly WorkItem[],
    crmDeals: readonly CrmDealCalendarEntry[] = [],
  ): HerxCalendarItem[] {
    return [
      ...this.projectWorkItems(workItems),
      ...this.projectCrmDeals(crmDeals),
    ];
  }
}

export const globalCalendarBridge = new CalendarWorkItemBridge();
